import React from "react";
import { Chip, IconButton, Stack, Typography } from "@mui/material";

import {
  HubHookFormInput,
  HubHookFormSelect,
  HubIcon,
  UserDetailIcon,
} from "../../components";
import { UsersHookFormSelect } from "../../components/UsersHookFormSelect";
import { stringToAlphabetSum } from "../../helpers/userImageColor";
import { LEAD_STAGES, LEAD_STAGE_LABELS } from "../../constants/salesStages";

import {
  StyledInfoGrid,
  StyledInlineFields,
  StyledProgressBar,
} from "./styles";

const SOURCES = [
  { id: "Referral", label: "Referral" },
  { id: "Website", label: "Website" },
  { id: "Trade Show", label: "Trade Show" },
  { id: "Cold Call", label: "Cold Call" },
  { id: "Other", label: "Other" },
];

const CellLabel = ({ children }) => (
  <Typography variant="caption" fontSize={9} color="text.secondary">
    {children}
  </Typography>
);

const getProgress = (stage) => {
  const total = Object.keys(LEAD_STAGES).length - 1;
  if (stage >= LEAD_STAGES.WON) return 100;
  return Math.round((stage / (total - 1)) * 100);
};

const LeadDetails = ({ lead, isNew = false }) => {
  if (!lead && !isNew) return null;

  const stage =
    LEAD_STAGE_LABELS.find((s) => s.id === lead?.stage) || LEAD_STAGE_LABELS[0];
  const progress = getProgress(lead?.stage || 0);
  const name = lead?.companyName || lead?.customer?.name || "Unnamed";

  return (
    <Stack gap="20px">
      {/* Header */}
      <StyledInlineFields>
        <Stack direction="row" gap="5px" alignItems="center">
          <UserDetailIcon
            size={44}
            user={{ name, hueShift: stringToAlphabetSum(name) }}
            includeName={false}
            variant="rounded"
          />
          <Stack>
            <HubHookFormInput
              name="companyName"
              placeholder="Company name"
              disableHelperText
              required
              startAdornment={<HubIcon icon="User" size={10} />}
              formControlProps={{
                sx: { input: { fontSize: "16px !important" } },
              }}
            />
            <HubHookFormInput
              name="contactEmail"
              placeholder="email@example.com"
              disableHelperText
              isEmail
              startAdornment={<HubIcon icon="Mail" size={10} />}
            />
          </Stack>
          <Stack ml="auto" direction="row" gap="4px">
            {lead?.contactEmail && (
              <IconButton
                size="small"
                href={`mailto:${lead.contactEmail}`}
                sx={{
                  border: "1px solid",
                  borderColor: "hubColors.greyLight",
                }}
              >
                <HubIcon icon="Mail" size={14} />
              </IconButton>
            )}
            {lead?.contactPhone && (
              <IconButton
                size="small"
                href={`tel:${lead.contactPhone}`}
                sx={{
                  border: "1px solid",
                  borderColor: "hubColors.greyLight",
                }}
              >
                <HubIcon icon="Phone" size={14} />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </StyledInlineFields>

      <StyledInfoGrid>
        <Stack>
          <CellLabel>Contact</CellLabel>
          <HubHookFormInput
            name="contactName"
            placeholder="Name"
            disableHelperText
            startAdornment={<HubIcon icon="User" size="10px" />}
          />
        </Stack>
        <Stack>
          <CellLabel>Phone</CellLabel>
          <HubHookFormInput
            name="contactPhone"
            placeholder="(000) 000-0000"
            disableHelperText
            startAdornment={<HubIcon icon="Phone" size="10px" />}
          />
        </Stack>
      </StyledInfoGrid>

      <StyledInfoGrid>
        <Stack>
          <CellLabel>Lead Owner</CellLabel>
          <UsersHookFormSelect
            name="assigneeId"
            label={null}
            disableHelperText
          />
        </Stack>
        <Stack>
          <CellLabel>Source</CellLabel>
          <HubHookFormSelect
            name="source"
            options={SOURCES}
            disableHelperText
            useDefault={false}
          />
        </Stack>
        <Stack>
          <CellLabel>Est. Value</CellLabel>
          <HubHookFormInput
            name="value"
            placeholder="0"
            isCurrency
            disableHelperText
          />
        </Stack>
        <Stack>
          <CellLabel>Probability</CellLabel>
          <HubHookFormInput
            name="probability"
            placeholder="0"
            disableHelperText
            endAdornment="%"
          />
        </Stack>
      </StyledInfoGrid>

      <Stack
        gap="8px"
        sx={{
          padding: "14px 16px",
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "hubColors.greyLight",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" gap="6px" alignItems="center">
            <HubIcon icon="Activity" size={14} />
            <Typography variant="boldCaptionL" fontSize={12}>
              Progress
            </Typography>
          </Stack>
          <Chip
            variant="colorTag"
            flow={stage?.flow}
            label={stage?.label}
            size="small"
            sx={{ fontSize: 10 }}
          />
        </Stack>
        <Stack direction="row" gap="8px" alignItems="center">
          <StyledProgressBar
            variant="determinate"
            value={progress}
            sx={{ flex: 1 }}
            color={lead?.stage === LEAD_STAGES.LOST ? "error" : "primary"}
          />
          <Typography variant="caption11">{progress}%</Typography>
        </Stack>
      </Stack>

      <Stack gap="4px">
        <HubHookFormInput
          name="notes"
          label="Notes"
          placeholder="Add notes..."
          multiline
          rows={3}
          disableHelperText
        />
      </Stack>
    </Stack>
  );
};

export default LeadDetails;
